// Rutas de la tienda y sus guardias: primero la marca (si la tienda existe y está abierta), después la sesión, el
// perfil y el negocio con que se pide. El catálogo (inicio, buscar, categorías, ficha y carrito) también se ve sin
// cuenta si la tienda lo abre (decisión D49); el resto pide entrar. El preventista (decisión D60) entra con su usuario
// del personal y arranca por "Mis visitas de hoy": pide para el cliente que visita, uno de su cartera.
import { useToast } from '@reparto/ui';
import { lazy, type ReactNode, Suspense, useEffect, useRef, useState } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  ScrollRestoration,
  useLocation,
  useNavigate,
} from 'react-router';
import { AvisoEnRevision } from './componentes/AvisoEnRevision';
import { Cargando } from './componentes/base';
import { PantallaCargando, PantallaMensajesCargando, PantallaModificarCargando } from './componentes/Esqueletos';
import { alNegarAcceso, mensajeDeError } from './datos/api';
import { cargarPerfil, entrarConEnlace, negocioDe, tomarEnlaceDeAcceso, useCuenta } from './datos/cuenta';
import { abrirCarrito, VISITANTE } from './datos/estado-carrito';
import { catalogoAbierto } from './datos/fuente';
import { cargarMarca, useMarca } from './datos/marca';
import { useSesion } from './datos/sesion';
import { Carrito } from './pantallas/Carrito';
import { Catalogo, Categorias } from './pantallas/Catalogo';
import { Confirmado } from './pantallas/Confirmado';
import { ElegirNegocio } from './pantallas/ElegirNegocio';
import { Habituales } from './pantallas/Habituales';
import { Clave, Olvide } from './pantallas/Clave';
import { CrearCuenta } from './pantallas/CrearCuenta';
import { Entrar } from './pantallas/Entrar';
import { EntrarCelular } from './pantallas/EntrarCelular';
import { EntrarVendedor } from './pantallas/EntrarVendedor';
import { Inicio } from './pantallas/Inicio';
import { Pedido } from './pantallas/Pedido';
import { Comprobante } from './pantallas/Comprobante';
import { Pedidos } from './pantallas/Pedidos';
import { Perfil } from './pantallas/Perfil';
import { Privacidad } from './pantallas/Privacidad';
import { Producto } from './pantallas/Producto';
import { VisitaCliente, Visitas } from './pantallas/Visitas';
import { VueltaGoogle } from './pantallas/VueltaGoogle';
import { ErrorMarca, SinAcceso, TiendaCerrada, TiendaNoExiste } from './pantallas/Puertas';

// "Completá tus datos" se usa una sola vez por cliente (decisión D48): se carga aparte, con su mapa.
const Registro = lazy(() => import('./pantallas/Registro').then((m) => ({ default: m.Registro })));
// Mensajes con la distribuidora (D53) y modificar un pedido que espera (D52): también aparte.
const Mensajes = lazy(() => import('./pantallas/Mensajes').then((m) => ({ default: m.Mensajes })));
const ModificarPedido = lazy(() => import('./pantallas/ModificarPedido').then((m) => ({ default: m.ModificarPedido })));
// La guía de uso (/uso), con sus capturas y sus estilos: aparte, para no pesar en la primera carga.
const Uso = lazy(() => import('./pantallas/Uso').then((m) => ({ default: m.Uso })));

/** Volver a traer el perfil al volver a la app después de un rato: las fechas de entrega dependen de la hora. */
const REFRESCO_PERFIL_MS = 10 * 60_000;

// El enlace de WhatsApp (#acceso=…) sirve un solo uso: cada token se canjea una vez aunque la pantalla se monte dos.
const canjes = new Map<string, Promise<string | null>>();
function canjear(token: string): Promise<string | null> {
  let p = canjes.get(token);
  if (!p) {
    p = entrarConEnlace(token).then(
      () => null,
      (e: unknown) => mensajeDeError(e),
    );
    canjes.set(token, p);
  }
  return p;
}
// Apenas abre la app, sin esperar a la marca.
const enlaceInicial = tomarEnlaceDeAcceso();
if (enlaceInicial) void canjear(enlaceInicial);

function Raiz() {
  const estado = useMarca((s) => s.estado);
  const abierta = useMarca((s) => s.marca?.abierta);
  const navegar = useNavigate();
  const { pathname } = useLocation();
  const [enlace, setEnlace] = useState(enlaceInicial);

  useEffect(() => {
    void cargarMarca();
    const alVolverLaSenal = () => void cargarMarca();
    // Con la tienda ya abierta en la pestaña, el enlace llega como un cambio del "#".
    const alCambiarHash = () => {
      const t = tomarEnlaceDeAcceso();
      if (t) setEnlace(t);
    };
    window.addEventListener('online', alVolverLaSenal);
    window.addEventListener('hashchange', alCambiarHash);
    return () => {
      window.removeEventListener('online', alVolverLaSenal);
      window.removeEventListener('hashchange', alCambiarHash);
    };
  }, []);

  useEffect(() => {
    if (!enlace) return;
    let vivo = true;
    void canjear(enlace).then((error) => {
      if (!vivo) return;
      setEnlace(null);
      if (error) navegar('/entrar', { replace: true, state: { error } });
      else navegar('/', { replace: true });
    });
    return () => {
      vivo = false;
    };
  }, [enlace, navegar]);

  if (estado === 'cargando') return <Cargando texto="Abriendo la tienda…" />;
  if (estado === 'no_existe') return <TiendaNoExiste />;
  if (estado === 'error') return <ErrorMarca />;
  // La política de privacidad se ve aunque la tienda esté cerrada: es la dirección que se anota en Google.
  if (abierta === false && pathname !== '/privacidad') return <TiendaCerrada />;
  if (enlace) return <Cargando texto="Entrando…" />;
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

function SinSesion() {
  const sesion = useSesion((s) => s.sesion);
  return sesion ? <Navigate to="/" replace /> : <Outlet />;
}

function ConSesion() {
  return (
    <GuardiaSesion>
      <Outlet />
    </GuardiaSesion>
  );
}

/**
 * El catálogo: con sesión, con la guardia de siempre (precios del negocio); sin cuenta, como visitante si la tienda abre
 * el catálogo (decisión D49), con su carrito guardado en el navegador. Si no, a entrar.
 */
function Abierta() {
  const sesion = useSesion((s) => !!s.sesion);
  const abierto = useMarca((s) => catalogoAbierto(s.marca));
  useEffect(() => {
    if (!sesion && abierto) abrirCarrito(VISITANTE);
  }, [sesion, abierto]);
  if (sesion)
    return (
      <GuardiaSesion>
        <Outlet />
      </GuardiaSesion>
    );
  if (!abierto) return <Navigate to="/entrar" replace />;
  return <Outlet />;
}

function GuardiaSesion({ children }: { children: ReactNode }) {
  const sesion = useSesion((s) => s.sesion);
  const perfil = useCuenta((s) => s.perfil);
  const estado = useCuenta((s) => s.estado);
  const negocioId = useCuenta((s) => s.negocioId);
  const { pathname } = useLocation();
  const { mostrar } = useToast();
  const ultimaCarga = useRef(0);
  // Quién entró: su cuenta (correo, Google, Apple o Facebook), su teléfono (decisiones D48 y D51) o el preventista (D60).
  const telefono = sesion?.telefono ?? sesion?.identidad_id ?? sesion?.vendedor?.id;
  const vendedor = !!sesion?.vendedor;
  const sinRed = useCuenta((s) => s.sinRed);

  useEffect(() => {
    if (!telefono) return;
    ultimaCarga.current = Date.now();
    void cargarPerfil();
  }, [telefono]);

  useEffect(() => {
    // Un 403 en cualquier pantalla (tienda cerrada, número sin acceso, negocio que ya no es suyo): se mira el perfil.
    const fin = alNegarAcceso(() => void cargarPerfil());
    const alVolver = () => {
      if (document.visibilityState !== 'visible' || Date.now() - ultimaCarga.current < REFRESCO_PERFIL_MS) return;
      ultimaCarga.current = Date.now();
      void cargarPerfil();
    };
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('online', alVolver);
    return () => {
      fin();
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('online', alVolver);
    };
  }, []);

  useEffect(() => {
    if (sinRed && perfil)
      mostrar('Sin señal: te mostramos lo último que guardamos.', { tipo: 'info', icono: 'signal_disconnected' });
  }, [sinRed]);

  const negocio = negocioDe(perfil, negocioId);
  useEffect(() => abrirCarrito(negocio?.id ?? null), [negocio?.id]);

  if (!sesion) return <Navigate to="/entrar" replace />;
  if (estado === 'bloqueado') return <SinAcceso />;
  // Mientras llega la cuenta (la primera vez en este celular o al volver de entrar): el esqueleto de la pantalla a la
  // que se va, con su marco. Con la cuenta guardada en el celular no aparece.
  if (!perfil) return estado === 'error' ? <SinAcceso /> : <PantallaCargando ruta={pathname} />;
  const enVisitas = pathname === '/visitas' || pathname.startsWith('/visitas/');
  // El preventista elige el cliente desde sus visitas (no desde "¿Para qué negocio pedís?"); sin cliente elegido,
  // lo que es de un cliente (el catálogo con sus precios, el carrito, los pedidos) lleva a las visitas.
  if (vendedor) {
    // Elegir negocio, el registro, los mensajes y modificar un pedido son del comercio.
    if (['/negocio', '/registro', '/mensajes'].includes(pathname) || pathname.endsWith('/modificar'))
      return <Navigate to="/visitas" replace />;
    if (!negocio && !enVisitas && pathname !== '/perfil') return <Navigate to="/visitas" replace />;
    return <>{children}</>;
  }
  if (enVisitas) return <Navigate to="/" replace />;
  // Cuenta nueva: primero completa los datos de su negocio (decisiones D48 y D51).
  if (perfil.estado === 'falta_completar')
    return pathname === '/registro' ? <Outlet /> : <Navigate to="/registro" replace />;
  if (pathname === '/registro') return <Navigate to="/" replace />;
  if (!negocio && pathname !== '/negocio') return <Navigate to="/negocio" replace />;
  return (
    <>
      {negocio?.estado_tienda === 'pendiente' ? <AvisoEnRevision /> : null}
      {children}
    </>
  );
}

const enrutador = createBrowserRouter([
  {
    element: <Raiz />,
    children: [
      {
        element: <SinSesion />,
        children: [
          { path: '/entrar', element: <Entrar /> },
          // Correo y contraseña (decisión D51); el celular, solo si la empresa lo prendió.
          { path: '/crear-cuenta', element: <CrearCuenta /> },
          { path: '/olvide', element: <Olvide /> },
          { path: '/entrar/celular', element: <EntrarCelular /> },
          // El preventista, con su usuario del personal (decisión D60).
          { path: '/vendedor/entrar', element: <EntrarVendedor /> },
        ],
      },
      {
        element: <Abierta />,
        children: [
          { path: '/', element: <Inicio /> },
          { path: '/buscar', element: <Catalogo /> },
          { path: '/categoria/:nombre', element: <Catalogo /> },
          { path: '/categorias', element: <Categorias /> },
          { path: '/producto/:id', element: <Producto /> },
          { path: '/carrito', element: <Carrito /> },
        ],
      },
      {
        element: <ConSesion />,
        children: [
          { path: '/negocio', element: <ElegirNegocio /> },
          // Modo vendedor (decisión D60): sus visitas del día y la visita a cada cliente.
          { path: '/visitas', element: <Visitas /> },
          { path: '/visitas/:clienteId', element: <VisitaCliente /> },
          { path: '/lo-de-siempre', element: <Habituales /> },
          { path: '/pedidos', element: <Pedidos /> },
          { path: '/pedidos/:id', element: <Pedido /> },
          // Facturación (decisión D57): ver y descargar el comprobante del pedido.
          { path: '/pedidos/:id/comprobante/:comprobante', element: <Comprobante /> },
          {
            path: '/pedidos/:id/modificar',
            element: (
              <Suspense fallback={<PantallaModificarCargando />}>
                <ModificarPedido />
              </Suspense>
            ),
          },
          {
            path: '/mensajes',
            element: (
              <Suspense fallback={<PantallaMensajesCargando />}>
                <Mensajes />
              </Suspense>
            ),
          },
          { path: '/confirmado/:id', element: <Confirmado /> },
          { path: '/perfil', element: <Perfil /> },
          {
            path: '/registro',
            element: (
              <Suspense fallback={<Cargando texto="Abriendo…" />}>
                <Registro />
              </Suspense>
            ),
          },
        ],
      },
      // Con o sin sesión, aunque el catálogo no se vea sin cuenta.
      { path: '/privacidad', element: <Privacidad /> },
      {
        path: '/uso',
        element: (
          <Suspense fallback={<Cargando texto="Abriendo la guía…" />}>
            <Uso />
          </Suspense>
        ),
      },
      // "Elegí tu contraseña nueva" con el enlace: con o sin sesión (abre la cuenta del enlace).
      { path: '/clave', element: <Clave /> },
      // La vuelta de Google, Apple o Facebook: con o sin sesión (al vincular desde Perfil ya hay una).
      { path: '/entrar/:proveedor', element: <VueltaGoogle /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

// Al tocar un aviso con la tienda abierta, el service worker pide ir a esa pantalla (public/sw-push.js, decisión D53).
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator)
  navigator.serviceWorker.addEventListener('message', (e: MessageEvent<{ tipo?: string; url?: string }>) => {
    const url = e.data?.url;
    if (e.data?.tipo === 'abrir' && typeof url === 'string' && url.startsWith('/') && !url.startsWith('//'))
      void enrutador.navigate(url);
  });

export function App() {
  return <RouterProvider router={enrutador} />;
}
